import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Sailboat, Trophy, User, Bell, Shield, LogOut, UserCircle, Radio } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { bigWin } from "@/lib/celebrate";
import { toast } from "sonner";
import { fireNewNotifications, primeSeenIds, registerServiceWorker } from "@/lib/pushNotifications";
import { FanionBadge } from "@/components/FanionBadge";
import { Footer } from "@/components/Footer";
import { InstallAppBanner } from "@/components/InstallAppBanner";

const navItems = [
  { to: "/", label: "Accueil", icon: Home, testid: "nav-home" },
  { to: "/etapes", label: "Étapes", icon: Sailboat, testid: "nav-stages" },
  { to: "/classement", label: "Classement", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/mes-paris", label: "Pronos", icon: User, testid: "nav-mybets" },
  { to: "/profil", label: "Profil", icon: UserCircle, testid: "nav-profile" },
];

export function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const seenWinRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    // Register the service worker once (idempotent)
    registerServiceWorker();
    // Prime seen marker with newest existing win so we don't re-celebrate old wins
    const prime = async () => {
      try {
        const { data } = await api.get("/notifications");
        const wins = data.filter((n) =>
          n.type === "bet_won" || n.type === "champion_won" ||
          n.type === "podium" || n.type === "fanion_upgrade"
        );
        seenWinRef.current = wins[0]?.id || "__none__";
        // Also prime push notification "seen" ids to avoid firing OS notifs for old items
        primeSeenIds(data.map((n) => n.id));
      } catch (e) {
        seenWinRef.current = "__none__";
      }
    };
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!mounted) return;
        setUnread(data.filter((n) => !n.read).length);
        // Detect new wins/podium since last check
        const wins = data.filter((n) =>
          n.type === "bet_won" || n.type === "champion_won" ||
          n.type === "podium" || n.type === "fanion_upgrade"
        );
        if (seenWinRef.current && wins.length && wins[0].id !== seenWinRef.current) {
          // celebrate the newest win
          bigWin();
          toast.success(wins[0].title, { description: wins[0].message });
          await refreshUser();
        }
        if (wins.length) seenWinRef.current = wins[0].id;
        // Fire OS-level browser notifications for any new items (no-op if opt-out)
        fireNewNotifications(data);
      } catch (e) {
        // ignore
      }
    };
    (async () => {
      await prime();
      await load();
    })();
    const t = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [user, refreshUser]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Top Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[color:var(--seafoam)]/80 border-b border-slate-200/70"
        data-testid="app-header"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
            <img
              src="/branding/app_icon.png"
              alt="Ma Petite Yole"
              className="w-10 h-10 rounded-xl object-cover shadow-sm"
            />
            <div className="hidden sm:block">
              <div className="brand-wordmark text-[color:var(--deep)] text-[18px] leading-none">
                Ma Petite Yole
              </div>
              <div className="text-[9px] tracking-[0.28em] uppercase text-[color:var(--sunshine)] font-bold mt-0.5">
                {user?.league_name || "Prono. Vibré. Gagné."}
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            {user && (
              <>
                {user.role !== "admin" && (
                  <div
                    className="flex items-center gap-1.5 bg-[color:var(--deep)] text-white pl-2 pr-4 py-1.5 rounded-full"
                    data-testid="wallet-balance"
                    title={`Fanion : ${user.fanion_label || "Mousse"} (Niv. ${user.fanion_level || 1})`}
                  >
                    <FanionBadge level={user.fanion_level || 1} size="sm" />
                    <span className="text-[color:var(--sunshine)] font-bold text-sm ml-1">◆</span>
                    <span className="font-mono font-bold text-sm tabular-nums">
                      {user.wallet_points}
                    </span>
                    <span className="text-[10px] opacity-70 hidden sm:inline">PTS</span>
                  </div>
                )}
                <Link
                  to="/live"
                  data-testid="nav-live"
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-[color:var(--coral)] text-white text-[11px] sm:text-xs font-bold hover:scale-105 transition-transform"
                  title="Suivi en direct des yoles"
                >
                  <Radio className="h-3.5 w-3.5" />
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                  </span>
                  Live
                </Link>
                <Link
                  to="/notifications"
                  data-testid="nav-notifications"
                  className="relative p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-[color:var(--coral)] transition-colors"
                  title="Alertes"
                >
                  <Bell className="h-4 w-4" strokeWidth={2.4} />
                  {unread > 0 && (
                    <Badge
                      className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-[color:var(--coral)] text-white text-[9px] font-bold border-0"
                      data-testid="unread-badge"
                    >
                      {unread}
                    </Badge>
                  )}
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--sunshine)] text-[color:var(--deep)] text-xs font-bold hover:scale-105 transition-transform"
                  >
                    <Shield className="h-3.5 w-3.5" /> Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-[color:var(--coral)] transition-colors"
                  title="Déconnexion"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-10">
        <Outlet />
      </main>

      {/* Footer (legal + social) */}
      <Footer />

      {/* PWA install prompt (Chrome/Edge/Android + iOS Safari guide) */}
      <InstallAppBanner />

      {/* Bottom navigation (mobile-first) */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-white/85 border-t border-slate-200"
        data-testid="bottom-nav"
      >
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`relative flex flex-col items-center justify-center py-2.5 gap-1 text-[10px] font-semibold tracking-wide uppercase transition-colors ${
                  active ? "text-[color:var(--coral)]" : "text-slate-500"
                }`}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
                  {it.to === "/notifications" && unread > 0 && (
                    <Badge
                      className="absolute -top-2 -right-3 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-[color:var(--coral)] text-white text-[9px] font-bold border-0"
                      data-testid="unread-badge"
                    >
                      {unread}
                    </Badge>
                  )}
                </div>
                <span>{it.label}</span>
                {active && (
                  <span className="absolute -top-px left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-full bg-[color:var(--coral)]" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
